import { NavListItem, Sidebar } from '@osmosis/shared/types/learndb/learnData';
import type { ParsedQuery } from '../../../context/PagesProvider';

const first = (value?: string | string[]): string =>
  (Array.isArray(value) ? value[0] : value) || '';

/**  Helps build /embed/<concept> links. */
export const conceptToUrl = (concept: string): string =>
  concept.replace(/\s/g, '_').replace(/\//g, '');

/**  Reads the comma separated `concepts` query param used by LTI/embed callers. */
export const parseConceptList = (concepts?: string | string[]): string[] =>
  first(concepts)
    .split(',')
    .map((concept) => concept.trim())
    .filter(Boolean);

/**  Whether the embed should render the playlist sidebar. */
export const hasEmbedPlaylist = (query: ParsedQuery = {}): boolean =>
  parseConceptList(query.concepts).length > 0 ||
  first(query.from).includes('playlist');

/**  Builds the query string appended to every sidebar link so the page keeps the playlist, the auth code and the LTI flag. */
export const buildEmbedQuery = (query: ParsedQuery = {}): string => {
  const concepts = parseConceptList(query.concepts);
  const from = first(query.from);
  const auth = first(query.auth);
  const lti = first(query.lti);

  const params = new URLSearchParams();
  if (concepts.length) params.set('concepts', concepts.join(','));
  if (from) params.set('from', from);
  if (auth) params.set('auth', auth);
  if (lti) params.set('lti', lti);

  const search = params.toString();
  return search ? `?${search}` : '';
};

/**  Points a sidebar link at the embed route, replacing any query the learn page link came with. */
export const toEmbedLink = (link: string, embedQuery: string): string => {
  const [path] = link.split('?');
  return `${path.replace(/^\/learn\//, '/embed/')}${embedQuery}`;
};

/**  Rewrites the learn sidebar so every item links back into the embed route. */
export const toEmbedSidebar = (
  sidebar: Sidebar | undefined,
  embedQuery: string,
): Sidebar | null => {
  if (!sidebar?.tabs?.length) return null;

  return {
    ...sidebar,
    tabs: sidebar.tabs.map((tab) => ({
      ...tab,
      sections: tab.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          link: toEmbedLink(item.link, embedQuery),
        })),
      })),
    })),
  };
};

/**
 * Builds a sidebar out of the `concepts` query param. The embed is given the
 * concept titles only, so items carry synthetic ids and no watch data — the
 * same information the legacy concept-list sidebar rendered.
 */
export const buildConceptListSidebar = ({
  concepts,
  title,
  embedQuery,
}: {
  concepts: string[];
  title: string;
  embedQuery: string;
}): Sidebar | null => {
  if (!concepts.length) return null;

  const items: NavListItem[] = concepts.map((concept, index) => ({
    // Negative so it can never collide with a real learn node
    learnNodeID: -(index + 1),
    link: `/embed/${conceptToUrl(concept)}${embedQuery}`,
    name: concept,
    pageType: 'video',
    prime: false,
    imageUri: '',
    duration: 0,
    tags: [],
    watchData: { percent: 0 },
  }));

  return {
    title,
    currentTab: title,
    tabs: [{ id: 'concepts', title, sections: [{ title, items }] }],
  };
};

/**  Resolves the sidebar to render inside the embed for the current url. */
export const getEmbedSidebar = ({
  query = {},
  sidebar,
  playlistTitle,
}: {
  query?: ParsedQuery;
  sidebar?: Sidebar;
  playlistTitle: string;
}): Sidebar | null => {
  if (!hasEmbedPlaylist(query)) return null;

  const embedQuery = buildEmbedQuery(query);
  const concepts = parseConceptList(query.concepts);

  if (concepts.length) {
    return buildConceptListSidebar({
      concepts,
      title: playlistTitle,
      embedQuery,
    });
  }

  return toEmbedSidebar(sidebar, embedQuery);
};
